import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { EarlyWarningPage } from '@/features/nis2/components/EarlyWarningPage';
import { useNIS2IncidentFlag } from '@/features/incidents/nis2/api/nis2-incident-flag';
import { useOrganizations } from '@/hooks/use-organizations';

export const Route = createFileRoute('/_authenticated/nis2/incidents/$incidentId/early-warning')({
  beforeLoad: () => ({
    getTitle: () => 'NIS2 Early Warning (24h)',
  }),
  component: EarlyWarningRoute,
});

function EarlyWarningRoute() {
  const { incidentId } = Route.useParams();
  const navigate = useNavigate();
  const {
    organizations,
    isLoading: organizationsLoading,
    error: organizationsError,
  } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);
  const orgId = activeOrg?.id ?? '';

  const {
    data: nis2Flag,
    isLoading: flagLoading,
    error: flagError,
  } = useNIS2IncidentFlag({
    incidentId,
    enabled: Boolean(orgId),
  });

  if (organizationsLoading || flagLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!orgId || organizationsError) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-5xl">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Organization unavailable</AlertTitle>
            <AlertDescription>
              {organizationsError?.message ?? 'Select an active organization to continue.'}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (flagError) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-5xl">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unable to load NIS2 mapping</AlertTitle>
            <AlertDescription>{flagError.message}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (!nis2Flag?.flagId) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-5xl space-y-6">
          <Button
            variant="ghost"
            onClick={() => {
              void navigate({ to: '/incidents/$incidentId', params: { incidentId } });
            }}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to incident
          </Button>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Incident is not flagged as NIS2</AlertTitle>
            <AlertDescription>
              Flag the incident as NIS2-reportable first. The 24-hour early warning form uses the
              NIS2 incident record created during that step.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <Button
          variant="ghost"
          onClick={() => {
            void navigate({ to: '/incidents/$incidentId', params: { incidentId } });
          }}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to incident
        </Button>

        <EarlyWarningPage orgId={orgId} incidentId={incidentId} nis2IncidentId={nis2Flag.flagId} />
      </div>
    </div>
  );
}
